/**
 * Single Kanban column — header with status name + count, scrollable card list.
 *
 * The drop target of a pointer-driven card drag (`useKanbanCardDrag`), found
 * by its `data-kanban-status`. Every column takes any card.
 */

import { cn } from "@/lib/cn";
import type { Task, TaskStatus } from "@/api/tasks";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { TASK_STATUS_LABEL_KEYS } from "./task-metadata-chips";
import { KanbanCard } from "./kanban-card";
import { KANBAN_COLUMN_MIN_W } from "./kanban-columns";

/** Every column shares the strip, never narrower than its readable minimum. */
const COLUMN_STYLE = { minWidth: KANBAN_COLUMN_MIN_W };

interface KanbanColumnProps {
  status: TaskStatus;
  tasks: Task[];
  onSelectTask: (taskId: string) => void;
  /** A card is being dragged somewhere on the board. */
  isDragging?: boolean;
  /** The dragged card is over this column. */
  isDropTarget?: boolean;
  onDragStartTask?: (task: Task) => void;
  onDragOverStatus?: (status: TaskStatus | null) => void;
  onDragEndTask?: (task: Task, status: TaskStatus | null) => void;
}

export function KanbanColumn({
  status,
  tasks,
  onSelectTask,
  isDragging = false,
  isDropTarget = false,
  onDragStartTask,
  onDragOverStatus,
  onDragEndTask,
}: KanbanColumnProps) {
  const t = useT(framesTaskboardMessages);

  return (
    <div
      className={cn(
        "group/column flex flex-1 flex-col rounded-preview transition-colors",
        isDropTarget && "bg-[var(--color-interactive-hover)]",
        isDragging &&
          !isDropTarget &&
          "outline-1 outline-dashed outline-[var(--color-interactive-focus)]",
      )}
      style={COLUMN_STYLE}
      data-kanban-status={status}
      data-testid={`kanban-column-${status}`}
      data-task-pocket-lane="column"
      data-drop-target={isDropTarget ? "true" : "false"}
    >
      {/* Column header */}
      <div className="flex shrink-0 items-center gap-1 px-1.5 py-1.5">
        <span className="min-w-0 flex-1 truncate text-appkit-caption font-semibold uppercase tracking-[0.08em] text-muted">
          {t(TASK_STATUS_LABEL_KEYS[status])}
        </span>
        <span className="shrink-0 text-appkit-small text-muted">
          {tasks.length}
        </span>
      </div>

      {/* Card list */}
      <div className="scrollbar-none nodrag min-h-0 flex-1 space-y-1 overflow-y-auto px-1.5 pb-1.5">
        {tasks.map((task) => (
          <KanbanCard
            key={task.id}
            task={task}
            onSelect={onSelectTask}
            onDragStartTask={onDragStartTask}
            onDragOverStatus={onDragOverStatus}
            onDragEndTask={onDragEndTask}
          />
        ))}
      </div>
    </div>
  );
}
